import * as React from 'react';
import { Button, Container, LinearProgress, MenuItem, Stack } from '@mui/material';
import { useTranslation } from "react-i18next";
import { observer } from 'mobx-react-lite';
import { AbilityContext } from '../../components/Can';
import Panel from '../panel/Panel';
import { useStores } from '../../stores';
import { useOpenDialog } from '../../hooks/useOpenDialog';
import HallMaintenanceTasksTable from './HallMaintenanceTasksTable';
import HallMaintenanceTaskDialog from './HallMaintenanceTaskDialog';
import { HallMaintenanceTask } from '../../domain/HallMaintenanceTask';
import { useConfirm } from 'material-ui-confirm';
import HallMaintenanceTaskViewDialog from './HallMaintenanceTaskViewDialog';
import { useNavigate } from 'react-router-dom';
import PanelHeaderSelect from '../../components/PanelHeaderSelect';
import moment from 'moment';
import { HallMaintenanceSort } from '../../stores/PanelStore';

const HallMaintenancePage = observer(() => {
    const { t } = useTranslation();
    const ability = React.useContext(AbilityContext);
    const { serverStore, hallMaintenanceStore, panelStore } = useStores();
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const confirm = useConfirm();
    const [selectedTask, setSelectedTask] = React.useState<HallMaintenanceTask | undefined>();
    const navigate = useNavigate();
    const sort = panelStore.hallMaintenancePage.sort;

    const canManage = ability.can('manage', 'HallMaintenance');

    React.useEffect(() => {
        hallMaintenanceStore.getTasks();
    }, [hallMaintenanceStore]);

    const openCreateDialog = () => {
        setSelectedTask(undefined);
        openDialog("manage-hall-maintenance-task");
    };

    const openViewDialog = (task: HallMaintenanceTask) => {
        setSelectedTask(task);
        openDialog("view-hall-maintenance-task");
    };

    const openEditDialog = (task: HallMaintenanceTask) => {
        setSelectedTask(task);
        openDialog("manage-hall-maintenance-task");
    };

    const handleCloseDialog = () => {
        setSelectedTask(undefined);
        closeDialog();
    };

    const deleteTask = (task: HallMaintenanceTask | number) => {
        const taskId = typeof task === 'number' ? task : task.id;
        confirm({
            title: t("are_you_sure"),
            description: t("this_will_permanently_delete"),
            confirmationText: t("yes"),
            cancellationText: t("no")
        }).then(() => {
            hallMaintenanceStore.deleteTask(taskId, handleCloseDialog);
        });
    };

    const openAssignments = (task: HallMaintenanceTask) => {
        navigate(`/hall-maintenance/tasks/${task.id}/assignments`);
    };

    const compareByCardNumber = (a: HallMaintenanceTask, b: HallMaintenanceTask) => {
        const cardA = a.cardNumber;
        const cardB = b.cardNumber;

        if (!cardA && !cardB) return 0;
        if (!cardA) return 1;
        if (!cardB) return -1;

        return cardA.localeCompare(cardB, undefined, { numeric: true, sensitivity: 'base' });
    };

    const getMonthsUntilNextPlannedMonth = (task: HallMaintenanceTask) => {
        if (!task.plannedMonths || task.plannedMonths.length === 0) {
            return Number.POSITIVE_INFINITY;
        }

        const currentMonth = moment().month() + 1;
        const normalizedMonths = task.plannedMonths.filter(month => month >= 1 && month <= 12);

        if (normalizedMonths.length === 0) {
            return Number.POSITIVE_INFINITY;
        }

        return Math.min(
            ...normalizedMonths.map(month => (month - currentMonth + 12) % 12)
        );
    };

    const sortedTasks = React.useMemo(() => {
        const tasks = [...hallMaintenanceStore.tasks];

        switch (sort) {
            case "category":
                return tasks.sort((a, b) => {
                    const categoryCompare = t(a.category).localeCompare(t(b.category));
                    if (categoryCompare !== 0) return categoryCompare;
                    return compareByCardNumber(a, b);
                });
            case "actual":
                return tasks.sort((a, b) => {
                    const distanceA = getMonthsUntilNextPlannedMonth(a);
                    const distanceB = getMonthsUntilNextPlannedMonth(b);

                    if (distanceA !== distanceB) return distanceA - distanceB;
                    return compareByCardNumber(a, b);
                });
            case "standard":
            default:
                return tasks.sort(compareByCardNumber);
        }
    }, [hallMaintenanceStore.tasks, sort, t]);

    const sortOptions = [
        { value: "standard", labelKey: "hall_maintenance_sort_standard" },
        { value: "category", labelKey: "hall_maintenance_sort_category" },
        { value: "actual", labelKey: "hall_maintenance_sort_actual" },
    ];

    const headerItems = (
        <Stack direction="row" spacing={2} alignItems="center">
            <PanelHeaderSelect
                key="hall-maintenance-sort-dropdown"
                value={sort}
                onChange={(value) => panelStore.setHallMaintenanceSort(value as HallMaintenanceSort)}
                options={sortOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                    </MenuItem>
                ))}
            />
            {canManage && (
                <Button
                    key="create-task"
                    type="button"
                    color="inherit"
                    onClick={openCreateDialog}
                >
                    {t("create_hall_maintenance_task")}
                </Button>
            )}
        </Stack>
    );

    return (
        <Panel
            title={t("hall_maintenance")}
            headerItems={headerItems}>

            <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
                {serverStore.serverRequestLoading && <LinearProgress sx={{ mb: 2 }} />}
                <HallMaintenanceTasksTable
                    tasks={sortedTasks}
                    canManage={canManage}
                    onView={openViewDialog}
                    onEdit={openEditDialog}
                    onDelete={deleteTask}
                    onAssignments={openAssignments}
                />
            </Container>

            <HallMaintenanceTaskDialog
                isOpen={isOpen("manage-hall-maintenance-task")}
                onClose={handleCloseDialog}
                onDelete={deleteTask}
                task={selectedTask!}
            />
            <HallMaintenanceTaskViewDialog
                isOpen={isOpen("view-hall-maintenance-task")}
                onClose={handleCloseDialog}
                task={selectedTask!}
            />
        </Panel >
    );
});

export default HallMaintenancePage;
